// 게임 화면 (5단계 모바일 UI, docs/ux/draft-v3): 3D는 화면 전체, UI는 그 위에 작게.
// 첫 화면에서 방을 고르고, 위 바(나가기·방 이름·Leaf), 아래 바(Go to · Actions · Talk · Grooming), 반원 동작 메뉴, 방 메뉴, 알림.
// 장소는 Go to를 쓰지 않아도 탭만으로 갈 수 있다 (문·탕·바·매트 등은 각 방의 탭 규칙이 처리).
import * as THREE from 'three';
import { CHAT_MAX, MENUS, isAllowedChat, type DishId, type ItemId, type LagoonMaskId } from '@capy/shared';
import { createStage } from './engine/renderer';
import { LEGACY, createLights } from './engine/lights';
import { createFollowCamera } from './engine/camera';
import { attachInput, hitGround, rayFrom } from './engine/input';
import { createTapMarker } from './engine/tapMarker';
import { startEmote } from './capy/pose';
import { checkClimb } from './capy/stackRules';
import { hopDown, shakeOff, startClimb, type LandingFn } from './capy/stacking';
import { setFace, setHand, setHead, type FaceKey } from './capy/equip';
import { setFur, setSize, type Capy, type EmoteKey } from './capy/agent';
import type { FurTone } from './capy/model';
import { cleanupEating, dropCarry } from './food/eating';
import type { NavGrid } from './engine/nav';
import { createQuietRoom } from './rooms/quiet';
import { tickSnow } from './rooms/quiet/nature';
import { quietTap, startBath, startExit, randomLand, releaseSlot, releaseWait } from './rooms/quiet/bots';
import { exitRoom, goIn, isDoorTap, orderDish, DOOR } from './rooms/quiet/ryokan';
import { createLagoonRoom, type LagoonNote, type LagoonRoom } from './rooms/lagoon';
import { PHRASES, applyBarItem, goToBar, goToRinse, lagoonTap, type BarJob } from './rooms/lagoon/bots';
import { SPAWN, lagoonRandom } from './rooms/lagoon/layout';
import { createJjimRoom, type JjimRoom } from './rooms/jjim';
import { deckTap, goDeck, goRoom, goToShop, jjimTap, placeOrder, playAt, startRollJ, toggleLieJ } from './rooms/jjim/bots';
import { AREAS, JDECK } from './rooms/jjim/layout';
import { createSpeech } from './ui/speech';
import { createShell, type Tab } from './ui/shell';
import { createWheel, type WheelItem } from './ui/wheel';
import { menuPanel, placesPanel, storeSheet, talkPanel, type Talk, type MenuItemView } from './ui/panels';
import { createGrooming } from './ui/grooming';
import { HUD_TEXT as TX, ITEM_TEXT, JJIM_TEXT, LAGOON_TEXT, MENU_TEXT, UI_TEXT, fill } from './text/en';

type RoomName = 'quiet' | 'lagoon' | 'jjim';
const MIST = 0xDCE5EC;
const canvas = document.getElementById('stage') as HTMLCanvasElement;
const view = document.querySelector('.view') as HTMLElement;
const stage = createStage(canvas, { clearColor: MIST, fog: [26, 52] });
const { scene, camera } = stage;
const lights = createLights(scene);
const now = () => stage.clock.getElapsedTime();

const room = createQuietRoom(scene);
const { R, ryokan, player } = room;
const cam = createFollowCamera(stage, lights, player.pos);
const marker = createTapMarker(room.world);
const speech = createSpeech(view);
const hit = new THREE.Vector3();
let cur: RoomName = 'quiet';
let lagoon: LagoonRoom | null = null;
let jjim: JjimRoom | null = null;
/** Leaf 잔액: 계정·결제 단계 전까지 0. */
const leaf = 0;

const navNow = (): NavGrid => (cur === 'lagoon' ? lagoon!.R.W.nav : cur === 'jjim' ? (player.size < 1 ? jjim!.S.navBaby : jjim!.S.nav) : R.nav);
const landingNow = (): LandingFn | undefined => (cur === 'quiet' ? R.landing : undefined);
const agentsNow = (): readonly Capy[] => (cur === 'lagoon' ? lagoon!.R.agents : cur === 'jjim' ? jjim!.R.agents : R.agents);
const jp = () => jjim!.player, lp = () => lagoon!.player;
const v3 = (p: { x: number; z: number }) => new THREE.Vector3(p.x, 0, p.z);
const ROOM_NAME = (k: RoomName) => UI_TEXT.goRoom[k].replace(/^\S+\s/u, '');

// ---------- 화면 틀 ----------
const shell = createShell(document.getElementById('game')!, {
  onExit: () => openHome(),
  onLeaf: () => openStore(null),
  onTab: (t) => toggleTab(t),
  onPick: (k) => { shell.home(null); if (k !== cur) switchRoom(k as RoomName); else enterRoomUi(); },
});
const wheel = createWheel(shell.layer);
const toast = (m: string, ms?: number) => shell.toast(m, ms);
function lagoonNote(key: LagoonNote) {
  const [a, b] = key.split('.') as [string, string?];
  const v = (LAGOON_TEXT as Record<string, unknown>)[a];
  toast(typeof v === 'string' ? v : (v as Record<string, string>)[b!]!, 4500);
}

// ---------- 탭: 내 카피바라 → 동작 메뉴, 다른 카피바라 → 올라타기, 그 밖 → 걷기·탕·문·바 (프로토타입 onTap 3916–3958) ----------
const deckPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -JDECK.H);
const hitsPlayer = (ray: THREE.Raycaster) => ray.intersectObjects([player.c.body, player.c.headG.children[0]!], false).length > 0;
attachInput(canvas, cam, {
  onTap(cx, cy) {
    if (shell.inHome) return;
    const ray = rayFrom(canvas, camera, cx, cy), t = now();
    if (hitsPlayer(ray)) { toggleTab('actions'); return; }
    if (wheel.open || dock === 'goto') closeTab();
    if (cur === 'quiet' && ryokan.indoors) return;
    const others = agentsNow().filter((o) => o !== player && (o as { mode?: string }).mode !== 'indoors' && o.c.root.visible);
    const hits = ray.intersectObjects(others.flatMap((o) => [o.c.body, o.c.headG.children[0]!]), false);
    if (hits.length) {
      const T = hits[0]!.object.userData.agent as Capy, why = checkClimb(player, T);
      player.enterAfter = false;
      if (why !== 'ok') { toast(why === 'full' ? UI_TEXT.full : UI_TEXT.refused); return; }
      const go = () => { startClimb(player, T); };
      if (player.riding || player.hop) hopDown(player, t, navNow(), go, landingNow()); else go();
      return;
    }
    if (cur === 'jjim') {
      if (dock === 'menu') closeDock();
      // 홀에서는 2층 데크 바닥(높이 JDECK.H)을 먼저 본다
      if (jp().area === 'hall' && ray.ray.intersectPlane(deckPlane, hit) && hit.x > JDECK.x0 && hit.x < JDECK.x1 && hit.z > JDECK.z0 && hit.z < JDECK.z1) { deckTap(jjim!.R, jp(), hit.x, hit.z); refresh(); return; }
      if (!hitGround(ray, hit)) return;
      const hx = hit.x, hz = hit.z, act = () => { if (jjimTap(jjim!.R, jp(), hx, hz)) marker.show(hx, hz); refresh(); };
      if (player.riding || player.hop) hopDown(player, t, navNow(), act); else act();
      return;
    }
    if (!hitGround(ray, hit)) return;
    player.enterAfter = false;
    const hx = hit.x, hz = hit.z;
    if (cur === 'lagoon') { lagoonTap(lagoon!.R, lp(), hx, hz, t); marker.show(hx, hz); refresh(); return; }
    const act = () => {
      if (isDoorTap(hx, hz)) { goIn(R, ryokan, player, now()); marker.show(DOOR.x, DOOR.z); return; }
      quietTap(R, player, hx, hz, now()); marker.show(hx, hz);
    };
    if (player.riding || player.hop) hopDown(player, t, R.nav, act, R.landing); else act();
  },
  // 누운 카피바라를 좌우로 밀면 구른다 (찜질방)
  swipeStart(cx, cy) { return cur === 'jjim' && !!player.lying && !shell.inHome && hitsPlayer(rayFrom(canvas, camera, cx, cy)); },
  onSwipe(dx) { if (Math.abs(dx) > 24) { startRollJ(jjim!.R, jp(), screenRight(dx), now()); refresh(); } },
});
/** 화면에서 오른쪽으로 민 것이 카피바라 기준 어느 쪽 구르기인지 (구르기 방향은 카피바라의 옆). */
function screenRight(dx: number): -1 | 1 {
  const right = new THREE.Vector3(Math.cos(player.facing), 0, -Math.sin(player.facing)), camRight = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
  return (right.dot(camRight) >= 0) === (dx > 0) ? 1 : -1;
}

// ---------- 아래 바 ----------
type Dock = 'goto' | 'talk' | 'menu' | null;
let dock: Dock = null, menuKind: 'ryokan' | 'bar' | 'shop' | null = null;
let sheet: 'grooming' | 'store' | null = null;
function closeDock() { dock = null; menuKind = null; shell.dock(null); }
function closeTab() { wheel.hide(); if (dock === 'goto' || dock === 'talk') closeDock(); shell.setTab(null); }
function toggleTab(t: Tab) {
  if (shell.tab === t) { closeTab(); return; }
  closeTab();
  if (t === 'talk' && cur === 'quiet') { toast(TX.quietNoTalk); return; }
  if (t === 'grooming') { openGrooming(); return; }
  shell.setTab(t);
  if (t === 'actions') showWheel();
  else if (t === 'goto') { dock = 'goto'; shell.dock(placesPanel(places())); }
  else if (t === 'talk') { dock = 'talk'; talk = talkPanel(sendChat, CHAT_MAX); shell.dock(talk.el); }
  refresh();
}

// ---------- Actions: 반원 (곡선 = 공통, 직선 = 이 방·지금 상황) ----------
function commonActions(): WheelItem[] {
  const a = player, t = () => now();
  return [
    { icon: 'wave', label: TX.act.wave, click: () => { startEmote(a, 'wave', t()); } },
    { icon: 'wobble', label: TX.act.wobble, click: () => { if (a.rider) shakeOff(a, t(), navNow(), undefined, landingNow()); startEmote(a, 'wobble', t()); } },
    { icon: 'nap', label: TX.act.nap, on: a.napping, click: () => { a.napping = !a.napping; a.emote = null; if (a.napping && a.mode === 'stroll') a.mode = 'idle'; } },
    { icon: 'yawn', label: TX.act.yawn, click: () => { startEmote(a, 'yawn' as EmoteKey, t()); } },
    { icon: 'climb', label: TX.act.climb, on: a.allowRide, click: () => { a.allowRide = !a.allowRide; if (!a.allowRide) shakeOff(a, t(), navNow(), undefined, landingNow()); toast(a.allowRide ? TX.climbOn : TX.climbOff); } },
  ];
}
function roomActions(): WheelItem[] {
  const a = player, out: WheelItem[] = [];
  if (a.riding || a.hop) out.push({ icon: 'hopdown', label: TX.act.hopdown, click: () => hopDown(a, now(), navNow(), undefined, landingNow()) });
  if (cur === 'quiet') {
    if (ryokan.indoors) out.push({ icon: 'goout', label: TX.act.goout, click: () => exitRoom(ryokan, player, cam, camera.aspect) });
    else {
      const bathing = a.mode === 'bathe';
      out.push({ icon: bathing ? 'getout' : 'soak', label: bathing ? TX.act.getout : TX.act.soak, click: () => quietBathe() });
      out.push({ icon: 'stroll', label: TX.act.stroll, on: a.mode === 'stroll', click: () => quietStroll() });
    }
  } else if (cur === 'lagoon') {
    const L = lp(), W = lagoon!.R.W;
    out.push({ icon: 'swim', label: TX.act.swim, click: () => { L.onArrive = null; L.napping = false; L.mode = 'goto'; L.wander = v3(lagoonRandom(W, L.pos, true)); } });
    out.push({ icon: 'stroll', label: TX.act.stroll, on: L.mode === 'stroll', click: () => { L.onArrive = null; L.napping = false; L.mode = L.mode === 'stroll' ? 'idle' : 'stroll'; L.wander = L.mode === 'stroll' ? v3(lagoonRandom(W, L.pos, null)) : null; } });
    if (a.mask && !a.washT) out.push({ icon: 'rinse', label: TX.act.rinse, click: () => { goToRinse(lagoon!.R, L, now()); if (L.onArrive === 'rinse') toast(LAGOON_TEXT.toRinse); } });
    if (a.carry) out.push({ icon: 'toss', label: TX.act.toss, click: () => { dropCarry(a, false, now()); toast(LAGOON_TEXT.tossed); } });
  } else {
    const J = jp();
    if (a.lying) {
      out.push({ icon: 'rollL', label: TX.act.roll, click: () => startRollJ(jjim!.R, J, -1, now()) });
      out.push({ icon: 'situp', label: TX.act.situp, click: () => toggleLieJ(jjim!.R, J, now()) });
      out.push({ icon: 'rollR', label: TX.act.roll, click: () => startRollJ(jjim!.R, J, 1, now()) });
    } else out.push({ icon: 'lie', label: TX.act.lie, click: () => { toggleLieJ(jjim!.R, J, now()); if (J.lying || J.freeLie) toast(TX.swipeRoll); } });
    if (a.handKey === 'scrubmitt') out.push({ icon: 'scrub', label: TX.act.scrub, click: () => { a.scrubT = now(); a.scratchT = 0; } });
    if (a.handKey === 'scratcher') out.push({ icon: 'scratch', label: TX.act.scratch, click: () => { a.scratchT = now(); a.scrubT = 0; } });
  }
  return out.slice(0, 4);
}
function showWheel() {
  // 어디로 움직이는 동작은 휠을 닫아 가는 모습을 보게 하고, 제자리 동작(감정·구르기 등)은 연달아 누를 수 있게 둔다
  const MOVES = new Set(['soak', 'getout', 'stroll', 'swim', 'rinse', 'hopdown', 'goout', 'toss']);
  const wrap = (list: WheelItem[]) => list.map((it) => ({ ...it, click: () => { player.enterAfter = false; it.click(); if (MOVES.has(it.icon)) closeTab(); refresh(); } }));
  wheel.show(wrap(commonActions()), wrap(roomActions()));
}
function quietBathe() {
  const a = player, t = now();
  if (a.riding || a.hop) { hopDown(a, t, R.nav, () => quietBathe(), R.landing); return; }
  if (a.mode === 'climb') { a.mode = 'idle'; a.climbT = null; }
  if (a.mode === 'bathe') startExit(R, a); else startBath(R, a, t);
}
function quietStroll() {
  const a = player;
  if (a.riding || a.hop) { hopDown(a, now(), R.nav, () => quietStroll(), R.landing); return; }
  if (a.mode === 'stroll') a.mode = 'idle';
  else { releaseSlot(a); releaseWait(a); a.settled = false; a.mode = 'stroll'; a.wander = randomLand(R); }
  a.napping = false;
}

// ---------- Go to: 이 방의 장소 버튼 ----------
function places() {
  const go = (fn: () => void) => () => { fn(); closeTab(); refresh(); };
  if (cur === 'quiet') {
    const P = TX.places.quiet, pool = (name: string) => {
      const list = R.L.pools.filter((q) => q.name === name);
      const q = list.sort((p1, p2) => Math.hypot(p1.x - player.pos.x, p1.z - player.pos.z) - Math.hypot(p2.x - player.pos.x, p2.z - player.pos.z))[0]!;
      return go(() => { if (ryokan.indoors) exitRoom(ryokan, player, cam, camera.aspect); quietTap(R, player, q.x, q.z, now()); marker.show(q.x, q.z); });
    };
    return [
      { label: P.big, click: pool('big') }, { label: P.mid, click: pool('mid') }, { label: P.solo, click: pool('solo') },
      { label: P.hidden, click: pool('hidden') }, { label: P.ryokan, click: go(() => { if (!ryokan.indoors) goIn(R, ryokan, player, now()); }), on: ryokan.indoors },
    ];
  }
  if (cur === 'lagoon') {
    const P = TX.places.lagoon, L = lp(), W = lagoon!.R.W;
    return [
      { label: P.swim, click: go(() => { L.onArrive = null; L.mode = 'goto'; L.wander = v3(lagoonRandom(W, L.pos, true)); }) },
      { label: P.mask, click: go(() => goToBar(lagoon!.R, L, 'mask', now())) },
      { label: P.slush, click: go(() => goToBar(lagoon!.R, L, 'slush', now())) },
      { label: P.rinse, click: go(() => goToRinse(lagoon!.R, L, now())) },
      { label: P.deck, click: go(() => { L.onArrive = null; L.mode = 'goto'; L.wander = v3(SPAWN); }) },
    ];
  }
  const J = jp();
  return [
    ...AREAS.map((k) => ({ label: JJIM_TEXT.rooms[k].replace(/^\S+\s/u, ''), on: J.area === k, click: go(() => goRoom(jjim!.R, J, k)) })),
    { label: JJIM_TEXT.deck.replace(/^\S+\s/u, ''), click: go(() => goDeck(jjim!.R, J)) },
    { label: JJIM_TEXT.shop.replace(/^\S+\s/u, ''), click: go(() => goToShop(jjim!.R, J)) },
    { label: TX.places.jjim.playground, click: go(() => playAt(jjim!.R, J, 'tramp')) },
  ];
}

// ---------- Talk: 8글자 ----------
let talk: Talk | null = null;
function sendChat(msg: string) {
  if (!isAllowedChat(msg)) return;
  speech.say(player, msg, now());
  // 가까운 카피바라가 가끔 대답한다
  if (Math.random() < 0.6) {
    const near = agentsNow().filter((o) => !o.isPlayer && o.c.root.visible && Math.hypot(o.pos.x - player.pos.x, o.pos.z - player.pos.z) < 9);
    if (near.length) { const o = near[Math.floor(Math.random() * near.length)]!; setTimeout(() => speech.say(o, PHRASES[Math.floor(Math.random() * PHRASES.length)]!, now()), 1200 + Math.random() * 1200); }
  }
}
window.addEventListener('keydown', (e) => {
  if (dock !== 'talk' || !talk || e.metaKey || e.ctrlKey || e.altKey) return;
  const k = e.key.toLowerCase();
  if (k.length === 1 && 'abcimpry'.includes(k)) { talk.type(k); e.preventDefault(); }
  else if (k === ' ') { talk.type('space'); e.preventDefault(); }
  else if (k === 'backspace') { talk.type('back'); e.preventDefault(); }
  else if (k === 'enter') { talk.type('send'); e.preventDefault(); }
});

// ---------- 방 메뉴: 료칸 식당, 라군 바, 찜질방 매점 ----------
const iconName = (label: string) => { const m = /^(\S+)\s(.*)$/u.exec(label); return m ? { icon: m[1]!, name: m[2]! } : { icon: '•', name: label }; };
function openMenu(kind: 'ryokan' | 'bar' | 'shop') {
  closeTab(); menuKind = kind; dock = 'menu'; renderMenu();
}
function renderMenu() {
  if (dock !== 'menu' || !menuKind) return;
  const close = () => { if (menuKind === 'ryokan' && ryokan.indoors) exitRoom(ryokan, player, cam, camera.aspect); if (menuKind === 'bar' && lagoon) lagoon.R.barOpen = null; closeDock(); refresh(); };
  if (menuKind === 'ryokan') {
    const items: MenuItemView[] = (Object.keys(MENUS.ryokan) as DishId[]).map((k) => ({ ...iconName(MENU_TEXT[k].label), label: iconName(MENU_TEXT[k].label).name, click: () => orderDish(player, k, room.world, now()) }));
    shell.dock(menuPanel(TX.menu.ryokan, items, close, { label: TX.act.goout, click: close }));
  } else if (menuKind === 'bar') {
    const job = lagoon!.R.barOpen;
    if (!job) { closeDock(); return; }
    const menu = job === 'mask' ? MENUS.maskbar : MENUS.slushbar;
    const items: MenuItemView[] = (Object.keys(menu) as (LagoonMaskId | DishId)[]).map((k) => {
      const lf = (menu as Record<string, { leaf: number }>)[k]!.leaf, n = iconName(MENU_TEXT[k].label);
      return { icon: n.icon, label: n.name, sub: lf ? `🍃${lf}` : LAGOON_TEXT.free, click: () => {
        if (lf > leaf) { openStore({ need: lf, item: n.name }); return; }
        applyBarItem(lagoon!.R, lp(), job, k, now()); toast(job === 'mask' ? LAGOON_TEXT.maskOn : LAGOON_TEXT.slushOn, 4500);
      } };
    });
    shell.dock(menuPanel(LAGOON_TEXT.barTitle[job], items, close));
  } else {
    const items: MenuItemView[] = (Object.keys(MENUS.jjimshop) as DishId[]).map((k) => {
      const n = iconName(MENU_TEXT[k].label);
      return { icon: n.icon, label: n.name, on: cart.includes(k), click: () => {
        const i = cart.indexOf(k);
        if (i >= 0) cart.splice(i, 1); else if (cart.length >= 4) { toast(JJIM_TEXT.shopMax); return; } else cart.push(k);
        renderMenu();
      } };
    });
    shell.dock(menuPanel(TX.menu.snack, items, close, { label: cart.length ? fill(JJIM_TEXT.orderN, { n: cart.length }) : JJIM_TEXT.order, click: () => {
      if (!cart.length) { toast(JJIM_TEXT.shopEmpty); return; }
      placeOrder(jjim!.R, jp(), cart.slice(), now()); closeDock(); refresh();
    } }));
  }
}
const cart: DishId[] = [];
function openShop() { cart.length = 0; openMenu('shop'); toast(JJIM_TEXT.shopPick, 4500); }

// ---------- Grooming & Leaf 상점 ----------
type Look = { head: Exclude<Capy['itemKey'], undefined>; face: FaceKey; hand: Exclude<Capy['handKey'], undefined>; size: 'normal' | 'baby'; fur: FurTone };
const look = (): Look => ({ head: player.itemKey ?? 'none', face: player.faceKey ?? 'none', hand: player.handKey ?? 'none', size: player.size < 1 ? 'baby' : 'normal', fur: player.tone });
const worldNow = () => (cur === 'lagoon' ? lagoon!.world : cur === 'jjim' ? jjim!.world : room.world);
const grooming = createGrooming({
  room: () => cur, player, look,
  wear(slot, key) {
    if (slot === 'head') setHead(player, key as Look['head'], now());
    else if (slot === 'face') setFace(player, key as FaceKey);
    else if (slot === 'hand') setHand(player, key as Look['hand'], cur, worldNow());
    else if (slot === 'size') setSize(player, key === 'baby' ? 0.5 : 1);
    else setFur(player, key as FurTone);
  },
  onClose() { sheet = null; shell.sheet(null); refresh(); },
  onGet(key: ItemId, lf: number) { openStore({ need: lf, item: ITEM_TEXT[key].name ?? key }); },
});
function openGrooming() { closeTab(); closeDock(); sheet = 'grooming'; shell.setTab('grooming'); grooming.open(); shell.sheet(grooming.el); }
function openStore(need: { need: number; item: string } | null) {
  wheel.hide();
  const back = sheet === 'grooming';
  sheet = 'store';
  shell.sheet(storeSheet(need ? fill(TX.store.need, { need: need.need, item: need.item, have: leaf }) : fill(TX.store.have, { have: leaf }),
    () => { if (back) { sheet = 'grooming'; shell.sheet(grooming.el); } else { sheet = null; shell.sheet(null); shell.setTab(null); } },
    () => toast(TX.store.notYet)));
}

// ---------- 상황 버튼 ----------
function contextAction() {
  if (dock || sheet || wheel.open) return null;
  const a = player;
  if (cur === 'quiet' && !ryokan.indoors) {
    if (a.mode === 'bathe') return { label: TX.act.getout, icon: 'getout' as const, click: () => { quietBathe(); refresh(); } };
    if (!a.riding && a.mode !== 'wait' && R.L.pools.some((P) => Math.hypot(P.x - a.pos.x, P.z - a.pos.z) < P.r + 3.5)) return { label: TX.act.soak, icon: 'soak' as const, click: () => { quietBathe(); refresh(); } };
  }
  if (cur === 'lagoon') {
    if (a.carry) return { label: TX.act.toss, icon: 'toss' as const, click: () => { dropCarry(a, false, now()); toast(LAGOON_TEXT.tossed); refresh(); } };
    if (a.mask && !a.washT) return { label: TX.act.rinse, icon: 'rinse' as const, click: () => { goToRinse(lagoon!.R, lp(), now()); refresh(); } };
  }
  return null;
}

// ---------- 매 0.3초: 버튼 상태 ----------
function refresh() {
  if (shell.inHome) return;
  if (wheel.open) showWheel();
  if (dock === 'goto') shell.dock(placesPanel(places()));
  shell.setCtx(contextAction());
}
setInterval(refresh, 300);

// ---------- 첫 화면 ----------
const TONES: Record<RoomName, string> = {
  quiet: 'linear-gradient(160deg,#F4F7FA 10%,#B9C9D6 60%,#8C6A4A)',
  lagoon: 'linear-gradient(160deg,#DDE7EE 10%,#7FD0D6 55%,#3A4A50)',
  jjim: 'linear-gradient(160deg,#4B3C5E 10%,#C99A5E 60%,#E8C48A)',
};
function openHome() {
  closeTab(); closeDock();
  if (sheet === 'grooming') grooming.close(); else if (sheet) { sheet = null; shell.sheet(null); }
  const count = (k: RoomName) => (k === 'quiet' ? R.agents.length : k === 'lagoon' ? (lagoon ? lagoon.R.agents.length : 30) : (jjim ? jjim.R.agents.length : 30));
  shell.home((['quiet', 'lagoon', 'jjim'] as RoomName[]).map((k) => ({ key: k, name: ROOM_NAME(k), blurb: UI_TEXT.rooms[k].blurb, count: `${Math.min(30, count(k))} / 30`, tone: TONES[k] })));
}

// ---------- 방 옮기기 (프로토타입 switchRoom 2738–2774) ----------
function switchRoom(name: RoomName) {
  if (name === cur) return;
  const a = player, t = now();
  if (cur === 'quiet' && ryokan.indoors) exitRoom(ryokan, player, cam, camera.aspect);
  shakeOff(a, t, navNow(), undefined, landingNow());
  const below = a.riding || (a.hop && a.hop.toBase); if (below) below.rider = null;
  a.riding = null; a.hop = null; a.rider = null; a.climbT = null;
  releaseSlot(a); releaseWait(a); cleanupEating(a); dropCarry(a, true, t);
  a.mode = 'idle'; a.wander = null; a.enterAfter = false; a.napping = false; a.emote = null; a.bathAmt = 0; a.washT = 0;
  speech.hide(agentsNow());
  if (lagoon) { lp().onArrive = null; lagoon.R.barOpen = null; lagoon.R.rinseUser = null; }
  if (cur === 'lagoon') lagoon!.day.reset();
  if (cur === 'jjim') { jjim!.leave(); endDineView(); }
  // 방 전용 소품은 그 방 밖에서 벗는다 (라군 팩, 찜질방 손 소품)
  if (a.faceKey && a.faceKey in MENUS.maskbar) setFace(a, a.wornFace ?? 'none');
  if (a.handKey && a.handKey !== 'none') setHand(a, 'none', name, scene);
  a.scratchT = 0; a.scrubT = 0;
  closeDock();
  cur = name;
  if (name === 'lagoon' && !lagoon) {
    lagoon = createLagoonRoom(scene, stage, lights, player, { say: (o, text, t2) => speech.say(o, text, t2), note: lagoonNote, onBar: () => openMenu('bar') });
  }
  if (name === 'jjim' && !jjim) {
    jjim = createJjimRoom(scene, stage, player, { say: (o, text, t2) => speech.say(o, text, t2), note: (m) => toast(m, 4500), hint: (m) => toast(m), onShop: openShop });
  }
  room.world.visible = name === 'quiet';
  room.outside.forEach((o) => { o.visible = o === room.snow.points ? name !== 'jjim' : name === 'quiet'; });
  if (lagoon) lagoon.world.visible = name === 'lagoon';
  if (jjim) jjim.world.visible = name === 'jjim';
  if (name === 'lagoon') lagoon!.enter();
  else if (name === 'jjim') jjim!.enter();
  else {
    room.world.add(a.c.root, a.ripple);
    a.pos.set(2.8, 0, 4.4); a.c.root.position.copy(a.pos); a.facing = a.targetFacing = 0; a.path = null;
  }
  // 찜질방은 실내 조명, 나머지는 낮 (라군은 들어간 뒤 낮밤이 바꾼다)
  if (name === 'jjim') { lights.hemi.intensity = 0.62 * LEGACY; lights.key.intensity = 0.42 * LEGACY; lights.key.color.set(0xFFE6C8); stage.fog?.color.set(0x2A2230); stage.renderer.setClearColor(0x14182E, 1); }
  else { lights.hemi.intensity = 0.55 * LEGACY; lights.key.intensity = 0.58 * LEGACY; lights.key.color.set(0xFFF1DE); stage.fog?.color.set(MIST); stage.renderer.setClearColor(MIST, 1); }
  worldNow().add(marker.mesh);
  cam.yaw = 0; cam.pitch = 0.32; cam.snap();
  canvas.setAttribute('aria-label', UI_TEXT.canvas[name]);
  enterRoomUi();
}
function enterRoomUi() {
  shell.setRoom(ROOM_NAME(cur));
  shell.setTalkLocked(cur === 'quiet');
  toast(cur === 'lagoon' ? LAGOON_TEXT.hint : cur === 'jjim' ? JJIM_TEXT.hint : UI_TEXT.hint, 4500);
  refresh();
}

// ---------- 찜질방 식탁에서 먹는 동안: 카메라를 식탁 쪽으로 낮게 (끝나면 원래대로) ----------
let savedCam: { yaw: number; pitch: number; dist: number } | null = null;
function endDineView() {
  if (!savedCam) return;
  cam.yaw = savedCam.yaw; cam.pitch = savedCam.pitch; cam.dist = savedCam.dist; savedCam = null; cam.dine = false; cam.snap();
}

// ---------- 시작: 첫 화면 ----------
shell.setLeaf(leaf);
shell.setRoom(ROOM_NAME(cur));
shell.setTalkLocked(true);
// ?room=quiet|lagoon|jjim: 첫 화면을 건너뛰고 바로 그 방으로 (공유 링크·확인용)
const startRoom = new URLSearchParams(location.search).get('room');
if (startRoom === 'quiet' || startRoom === 'lagoon' || startRoom === 'jjim') { if (startRoom !== cur) switchRoom(startRoom); else enterRoomUi(); }
else openHome();

let wasIndoors = false, barSeen: BarJob | null = null;
stage.onTick.add((t, dt) => {
  if (cur === 'quiet') {
    room.update(t, dt, cam);
    // 료칸에 들어가면 식당 메뉴, 나오면 닫기
    if (ryokan.indoors !== wasIndoors) { wasIndoors = ryokan.indoors; if (wasIndoors) { openMenu('ryokan'); toast(UI_TEXT.hintIndoors, 4500); } else if (menuKind === 'ryokan') closeDock(); refresh(); }
  } else if (cur === 'lagoon') {
    lagoon!.update(t, dt);
    tickSnow(room.snow, t, dt, cam.target.x, cam.target.z);
    const bar = lagoon!.R.barOpen;
    if (bar !== barSeen) { barSeen = bar; if (!bar && menuKind === 'bar') closeDock(); else if (bar) openMenu('bar'); }
  } else {
    jjim!.update(t, dt);
    if (jp().dineView) { if (!savedCam) { savedCam = { yaw: cam.yaw, pitch: cam.pitch, dist: cam.dist }; cam.snap(); } cam.yaw = player.facing; cam.pitch = 0.24; cam.dist = 6.2; cam.dine = true; }
    else endDineView();
  }
  if (sheet === 'grooming') grooming.update(t, dt);
  cam.lift = Math.max(shell.covered(), wheel.open ? 120 : 0);
  cam.update(dt, player.c.root);
  marker.update(dt);
  speech.update(t, agentsNow(), camera, view);
});
stage.start();

// 개발 중 브라우저 콘솔에서 상태를 볼 수 있게 (배포 빌드에는 빠진다)
if (import.meta.env.DEV) Object.assign(window, { __capy: { stage, room, player, cam, shell, get lagoon() { return lagoon; }, get jjim() { return jjim; }, switchRoom, openMenu, openShop } });
